import { useCallback, useState } from 'react';

import { TaskGroup, UpdateTaskGroupInput } from '@this+that/models';
import log from 'loglevel';

import { useUpdateTaskGroup as useUpdateTaskGroupService } from '../../services/taskGroups.service';
import { useInboxEntriesStore } from '../../store';

interface UseUpdateTaskGroupReturn {
  handleUpdateTaskGroup: (
    taskGroup: UpdateTaskGroupInput,
  ) => Promise<TaskGroup | undefined>;
  isUpdating: boolean;
  updateError: Error | null;
}

export function useUpdateTaskGroup(): UseUpdateTaskGroupReturn {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  const { updateTaskGroup: updateTaskGroupService } =
    useUpdateTaskGroupService();
  const { updateTaskGroup } = useInboxEntriesStore(
    (state) => state.taskManager.taskGroups,
  );

  const handleUpdateTaskGroup = useCallback(
    async (taskGroup: UpdateTaskGroupInput): Promise<TaskGroup | undefined> => {
      setIsUpdating(true);
      setUpdateError(null);
      try {
        const updatedTaskGroup = await updateTaskGroupService(taskGroup);
        if (updatedTaskGroup) {
          updateTaskGroup(updatedTaskGroup);
        }
        return updatedTaskGroup;
      } catch (error) {
        const errorInstance =
          error instanceof Error
            ? error
            : new Error('Failed to update task group');
        setUpdateError(errorInstance);
        log.error('Error updating task group:', error);
        return undefined;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateTaskGroupService, updateTaskGroup],
  );

  return {
    handleUpdateTaskGroup,
    isUpdating,
    updateError,
  };
}
